// src/lib/api.ts
import {
  AdminRole,
  UserRole,
  UserProfile,
  Ride,
  RideWithRequests,
  RideRequest,
  RiderRideRequest,
  CreateRiderRideRequestPayload,
  FeedPost,
  Message,
  Conversation,
  Notification,
  RideStatus,
  RiderRideStatus,
  DriverRideStatus,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://campus-connect.fly.dev/api';

console.log(`API using base URL: ${API_BASE_URL}`);

// ============ Typed Fetch Client ============

interface ApiRequestConfig {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | null | undefined>;
  timeout?: number;
  skipAuth?: boolean;
  suppressUnauthorizedRedirect?: boolean;
}

interface TypedApiClient {
  get: <T = any>(url: string, config?: ApiRequestConfig) => Promise<T>;
  post: <T = any>(url: string, data?: any, config?: ApiRequestConfig) => Promise<T>;
  put: <T = any>(url: string, data?: any, config?: ApiRequestConfig) => Promise<T>;
  delete: <T = any>(url: string, config?: ApiRequestConfig) => Promise<T>;
  patch: <T = any>(url: string, data?: any, config?: ApiRequestConfig) => Promise<T>;
}

export class ApiClientError extends Error {
  status?: number;
  code?: string;
  url?: string;
  responseBody?: string;

  constructor(
    message: string,
    status?: number,
    code?: string,
    options?: {
      url?: string;
      responseBody?: string;
    },
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.url = options?.url;
    this.responseBody = options?.responseBody;
  }
}

const DEFAULT_TIMEOUT_MS = 10000;
const MAGIC_LINK_VERIFY_TIMEOUT_MS = 30000;
const normalizedBaseUrl = API_BASE_URL.replace(/\/+$/, '');

const buildRequestUrl = (path: string, params?: ApiRequestConfig['params']) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const requestUrl = new URL(`${normalizedBaseUrl}${normalizedPath}`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        requestUrl.searchParams.set(key, String(value));
      }
    }
  }

  return requestUrl.toString();
};

const parseResponseBody = async (response: Response) => {
  if (response.status === 204) {
    return {
      body: undefined,
      rawText: undefined,
      parseError: undefined,
    };
  }

  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (!text) {
    return {
      body: undefined,
      rawText: undefined,
      parseError: undefined,
    };
  }

  if (contentType.includes('application/json')) {
    try {
      return {
        body: JSON.parse(text),
        rawText: text,
        parseError: undefined,
      };
    } catch (error) {
      return {
        body: undefined,
        rawText: text,
        parseError: error instanceof Error ? error : new Error('Failed to parse JSON response'),
      };
    }
  }

  return {
    body: text,
    rawText: text,
    parseError: undefined,
  };
};

const handleUnauthorized = () => {
  localStorage.removeItem('collegeryde_token');
  localStorage.removeItem('collegeryde_user');
  window.location.href = '/';
};

const request = async <T>(
  method: string,
  path: string,
  data?: unknown,
  config: ApiRequestConfig = {},
): Promise<T> => {
  const controller = new AbortController();
  const timeoutMs = config.timeout ?? DEFAULT_TIMEOUT_MS;
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  const token = config.skipAuth ? null : localStorage.getItem('collegeryde_token');
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...config.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const requestUrl = buildRequestUrl(path, config.params);

  console.log(`API ${method.toUpperCase()} ${requestUrl}`);

  try {
    const response = await fetch(requestUrl, {
      method,
      headers,
      credentials: 'include',
      body:
        data === undefined
          ? undefined
          : isFormData
            ? (data as FormData)
            : JSON.stringify(data),
      signal: controller.signal,
    });

    const { body: responseBody, rawText, parseError } = await parseResponseBody(response);

    if (parseError) {
      const parseFailureMessage = response.ok
        ? 'Server returned invalid JSON.'
        : `Server returned ${response.status} with invalid JSON.`;

      console.error('[API] Failed to parse JSON response:', {
        method: method.toUpperCase(),
        url: requestUrl,
        status: response.status,
        statusText: response.statusText,
        body: rawText,
        parseError: parseError.message,
      });

      throw new ApiClientError(parseFailureMessage, response.status, 'INVALID_JSON_RESPONSE', {
        url: requestUrl,
        responseBody: rawText,
      });
    }

    if (!response.ok) {
      const textBody = typeof responseBody === 'string' ? responseBody.trim() : '';
      const fallbackText = textBody
        ? textBody.slice(0, 200)
        : rawText?.trim()
          ? rawText.trim().slice(0, 200)
          : '';
      const message =
        (responseBody &&
        typeof responseBody === 'object' &&
        'error' in responseBody &&
        typeof responseBody.error === 'string' &&
        responseBody.error) ||
        (responseBody &&
        typeof responseBody === 'object' &&
        'message' in responseBody &&
        typeof responseBody.message === 'string' &&
        responseBody.message) ||
        fallbackText ||
        response.statusText ||
        'Request failed';

      if (response.status === 401 && !config.skipAuth && !config.suppressUnauthorizedRedirect) {
        handleUnauthorized();
      }

      console.error('[API] Request failed:', {
        method: method.toUpperCase(),
        url: requestUrl,
        status: response.status,
        statusText: response.statusText,
        body: responseBody ?? rawText,
      });

      throw new ApiClientError(message, response.status, undefined, {
        url: requestUrl,
        responseBody: rawText,
      });
    }

    return responseBody as T;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiClientError('Request timed out', undefined, undefined, {
        url: requestUrl,
      });
    }

    if (error instanceof Error) {
      throw new ApiClientError(error.message, undefined, undefined, {
        url: requestUrl,
      });
    }

    throw new ApiClientError('Request failed', undefined, undefined, {
      url: requestUrl,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const apiClient: TypedApiClient = {
  get: <T = any>(url: string, config?: ApiRequestConfig) => request<T>('GET', url, undefined, config),
  post: <T = any>(url: string, data?: any, config?: ApiRequestConfig) => request<T>('POST', url, data, config),
  put: <T = any>(url: string, data?: any, config?: ApiRequestConfig) => request<T>('PUT', url, data, config),
  delete: <T = any>(url: string, config?: ApiRequestConfig) => request<T>('DELETE', url, undefined, config),
  patch: <T = any>(url: string, data?: any, config?: ApiRequestConfig) => request<T>('PATCH', url, data, config),
};

// ============ API Response Types ============

// Auth responses
interface MagicLinkResponse {
  success: boolean;
  message: string;
  expires_in: number;
}

interface AuthResponse {
  success: boolean;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
    phone_number?: string;
    schoolDomain?: string;
    schoolName?: string;
    avatar?: string;
    major?: string;
    languages?: string[];
    favoriteMusic?: string;
    hobbies?: string[];
    rating?: number;
    ridesCompleted?: number;
  };
  token: string;
}

export interface SuperAdminStats {
  totalUsers: number;
  totalStudents: number;
  totalDrivers: number;
  totalRiders: number;
  totalRegisteredColleges: number;
  totalAdmins: number;
  totalSuperAdmins: number;
  totalRides: number;
  totalPosts: number;
  usersLast7Days: number;
  ridesLast7Days: number;
  postsLast7Days: number;
}

export interface SchoolBreakdown {
  schoolDomain: string;
  schoolName: string;
  totalUsers: number;
  totalStudents: number;
  totalAdmins: number;
  totalRides: number;
  totalPosts: number;
}

export interface SuperAdminDirectoryUser extends UserProfile {
  createdAt?: string;
}

export type ReminderScope =
  | 'all_users'
  | 'drivers'
  | 'riders'
  | 'admins'
  | 'school'
  | 'selected_users';

interface SuperAdminReminderConfig {
  availableScopes: ReminderScope[];
  sendEndpoint: string;
}

interface SuperAdminStatsResponse {
  success: boolean;
  stats: SuperAdminStats;
  users: {
    all: ApiRecord[];
    drivers: ApiRecord[];
    riders: ApiRecord[];
    admins: ApiRecord[];
    superAdmins: ApiRecord[];
  };
  schools: SchoolBreakdown[];
  reminders: SuperAdminReminderConfig;
}

export interface SchoolAdminDashboardStats {
  students: number;
  drivers: number;
  riders: number;
  rides: number;
  activeRides: number;
  posts: number;
}

interface SchoolAdminDashboardResponse {
  success: boolean;
  role: AdminRole;
  school: {
    schoolDomain: string;
    schoolName: string;
  };
  stats: SchoolAdminDashboardStats;
  students: ApiRecord[];
  rides: ApiRecord[];
  posts: ApiRecord[];
}

// Ride responses
interface RidesListResponse {
  success: boolean;
  rides: Ride[];
}

interface MyRidesResponse {
  success: boolean;
  rides: RideWithRequests[];
}

interface RideResponse {
  success: boolean;
  ride: Ride;
}

interface RideRequestResponse {
  success: boolean;
  request: RideRequest;
}

interface RideRequestsListResponse {
  success: boolean;
  requests: RideRequest[];
}

// Message responses
interface ConversationsResponse {
  success: boolean;
  conversations: Conversation[];
}

interface ConversationResponse {
  success: boolean;
  conversation: Conversation;
}

interface MessagesResponse {
  success: boolean;
  messages: Message[];
}

interface MessageResponse {
  success: boolean;
  message: Message;
}

// Feed responses
interface PostsResponse {
  success: boolean;
  posts: FeedPost[];
}

interface PostResponse {
  success: boolean;
  post: FeedPost;
}

// Notification responses
interface NotificationsResponse {
  success: boolean;
  notifications: Notification[];
}

// Profile responses
interface ProfileResponse {
  success: boolean;
  user: UserProfile;
}

// ============ Payload Types ============

export interface RegisterPayload {
  role: UserRole;
  name: string;
  email: string;
  phone_number: string;
  driver_license?: string;
  license_plate?: string;
}

export interface LoginPayload {
  email: string;
  role: UserRole;
}

export interface AdminLoginPayload {
  email: string;
  password?: string;
}

export interface AdminLoginResponse extends Partial<AuthResponse>, Partial<MagicLinkResponse> {
  success: boolean;
  message?: string;
  expires_in?: number;
}

export interface AdminListItem extends UserProfile {}

export interface CreateAdminPayload {
  name: string;
  email: string;
  password: string;
  phone_number?: string;
  school_name?: string;
}

export interface SchoolAdminDashboardData {
  role: AdminRole;
  school: {
    schoolDomain: string;
    schoolName: string;
  };
  stats: SchoolAdminDashboardStats;
  students: UserProfile[];
  rides: RideWithRequests[];
  posts: FeedPost[];
}

export interface SuperAdminStatsData {
  stats: SuperAdminStats;
  users: {
    all: SuperAdminDirectoryUser[];
    drivers: SuperAdminDirectoryUser[];
    riders: SuperAdminDirectoryUser[];
    admins: SuperAdminDirectoryUser[];
    superAdmins: SuperAdminDirectoryUser[];
  };
  schools: SchoolBreakdown[];
  reminders: SuperAdminReminderConfig;
}

export interface SendSuperAdminReminderPayload {
  scope: ReminderScope;
  subject: string;
  headline: string;
  message: string;
  schoolDomain?: string;
  role?: string;
  userIds?: string[];
  ctaLabel?: string;
  ctaUrl?: string;
}

export interface SendSuperAdminReminderResponse {
  success: boolean;
  message: string;
  scope: ReminderScope;
  subject: string;
  headline: string;
  targeted: number;
  sent: number;
  failed: number;
  failedEmails: string[];
}

export interface CreateRidePayload {
  destination: string;
  destinationName: string;
  pickupLocation: string;
  date: string;
  startTime: string;
  endTime: string;
  seatsAvailable: number;
  gasSplitCost: number;
}

export interface StartConversationPayload {
  userId: string;
  rideId?: string;
}

export interface SendMessagePayload {
  text: string;
}

export interface CreateFeedPostPayload {
  type: 'party' | 'trade';
  title: string;
  description: string;
  location?: string;
  date?: string;
  time?: string;
  price?: number;
  images?: string[];
}

export interface UpdateProfilePayload {
  name?: string;
  phone_number?: string;
  avatar?: string;
  major?: string;
  languages?: string[];
  favoriteMusic?: string;
  hobbies?: string[];
}

export type RideRequestAction = 'accept' | 'decline';

type MaybeWrappedList<T> = T[] | { success?: boolean; rides?: T[] };
type MaybeWrappedRide<T> = T | { success?: boolean; ride?: T };
type ApiRecord = Record<string, any>;
const riderStatuses = new Set(['pending', 'ongoing', 'ended']);
const driverStatuses = new Set(['pending', 'ongoing', 'completed']);

const deriveTripStatuses = (
  date?: string,
  startTime?: string,
  endTime?: string,
) => {
  if (!date || !startTime || !endTime) {
    return { rider: 'pending' as RiderRideStatus, driver: 'pending' as DriverRideStatus };
  }

  const now = new Date();
  const startDateTime = new Date(`${date}T${startTime}:00`);
  const endDateTime = new Date(`${date}T${endTime}:00`);

  if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
    return { rider: 'pending' as RiderRideStatus, driver: 'pending' as DriverRideStatus };
  }

  if (endDateTime <= now) {
    return { rider: 'ended' as RiderRideStatus, driver: 'completed' as DriverRideStatus };
  }

  if (startDateTime <= now) {
    return { rider: 'ongoing' as RiderRideStatus, driver: 'ongoing' as DriverRideStatus };
  }

  return { rider: 'pending' as RiderRideStatus, driver: 'pending' as DriverRideStatus };
};

export const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ApiClientError) {
    if (error.status === 401) return 'Your session expired. Please sign in again.';
    if (error.message) return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  return fallback;
};

const normalizeUser = (user?: ApiRecord): UserProfile => ({
  id: user?.id ?? '',
  name: user?.name ?? 'Unknown User',
  email: user?.email ?? '',
  phone_number: user?.phone_number ?? user?.phoneNumber ?? '',
  schoolDomain: user?.schoolDomain ?? user?.school_domain ?? '',
  schoolName: user?.schoolName ?? user?.school_name ?? '',
  avatar: user?.avatar ?? '',
  major: user?.major ?? '',
  languages: user?.languages ?? [],
  favoriteMusic: user?.favoriteMusic ?? user?.favorite_music ?? '',
  hobbies: user?.hobbies ?? [],
  rating: Number(user?.rating ?? 0),
  ridesCompleted: Number(user?.ridesCompleted ?? user?.rides_completed ?? 0),
  role: (user?.role ?? 'rider') as UserRole,
  driver_license: user?.driver_license,
  license_plate: user?.license_plate,
});

const normalizeRideRequest = (request?: ApiRecord): RideRequest => ({
  id: request?.id ?? '',
  rideId: request?.rideId ?? request?.ride_id ?? '',
  rider: normalizeUser(request?.rider),
  status: request?.status === 'declined' ? 'rejected' : (request?.status ?? 'pending'),
  requestedAt: request?.requestedAt ?? request?.requested_at ?? new Date().toISOString(),
  message: request?.message,
});

const normalizeRiderRideRequest = (request?: ApiRecord): RiderRideRequest => ({
  id: request?.id ?? '',
  rider: normalizeUser(request?.rider ?? request?.user),
  pickupLocation: request?.pickupLocation ?? request?.pickup_location ?? '',
  destinationName: request?.destinationName ?? request?.destination_name ?? request?.destination ?? '',
  date: request?.date ?? '',
  startTime: request?.startTime ?? request?.start_time ?? '',
  endTime: request?.endTime ?? request?.end_time ?? '',
  isEmergency: Boolean(request?.isEmergency ?? request?.is_emergency ?? false),
  status: request?.status ?? 'open',
  acceptedDriver: request?.acceptedDriver || request?.accepted_driver
    ? normalizeUser(request.acceptedDriver ?? request.accepted_driver)
    : undefined,
  createdAt: request?.createdAt ?? request?.created_at ?? new Date().toISOString(),
});

type RiderRideRequestsResponse =
  | ApiRecord[]
  | {
      success?: boolean;
      requests?: ApiRecord[];
      ride_requests?: ApiRecord[];
      rider_requests?: ApiRecord[];
      rideRequests?: ApiRecord[];
      data?: ApiRecord[] | { requests?: ApiRecord[]; ride_requests?: ApiRecord[] };
    };

const extractRiderRideRequests = (response: RiderRideRequestsResponse): ApiRecord[] => {
  if (Array.isArray(response)) return response;

  const data = response.data;
  if (Array.isArray(data)) return data;

  return response.requests
    ?? response.ride_requests
    ?? response.rider_requests
    ?? response.rideRequests
    ?? (data && !Array.isArray(data) ? data.requests ?? data.ride_requests : undefined)
    ?? [];
};

const normalizeMessage = (message?: ApiRecord): Message => ({
  id: message?.id ?? '',
  conversationId: message?.conversationId ?? message?.conversation_id ?? '',
  senderId: message?.senderId ?? message?.sender_id ?? '',
  text: message?.text ?? '',
  timestamp: message?.timestamp ?? message?.createdAt ?? message?.created_at ?? new Date().toISOString(),
  read: Boolean(message?.read),
});

const normalizeRide = (ride?: ApiRecord): RideWithRequests => {
  const driverRecord =
    ride?.driver ??
    ride?.user ??
    (ride?.driver_id || ride?.driverId || ride?.driver_name || ride?.driverName
      ? {
          id: ride?.driver_id ?? ride?.driverId ?? '',
          name: ride?.driver_name ?? ride?.driverName ?? '',
          email: ride?.driver_email ?? ride?.driverEmail ?? '',
          role: ride?.driver_role ?? ride?.driverRole ?? 'driver',
          avatar: ride?.driver_avatar ?? ride?.driverAvatar ?? '',
        }
      : undefined);
  const seatsAvailable = Number(ride?.seatsAvailable ?? ride?.seats_available ?? 0);
  const riders = Array.isArray(ride?.riders) ? ride.riders.map(normalizeUser) : [];
  const requests = Array.isArray(ride?.requests) ? ride.requests.map(normalizeRideRequest) : [];
  const date = ride?.date ?? '';
  const startTime = ride?.startTime ?? ride?.start_time ?? ride?.time ?? '';
  const endTime = ride?.endTime ?? ride?.end_time ?? ride?.time ?? '';
  const seatsTotal = Number(
    ride?.seatsTotal ??
      ride?.seats_total ??
      ride?.capacity ??
      ride?.totalSeats ??
      seatsAvailable + riders.length
  );
  const derivedStatuses = deriveTripStatuses(date, startTime, endTime);
  const normalizedStatus = (ride?.tripStatus ?? ride?.trip_status ?? ride?.status ?? 'pending') as RideStatus;
  const riderStatusValue = ride?.tripStatusRider ?? ride?.trip_status_rider;
  const driverStatusValue = ride?.tripStatusDriver ?? ride?.trip_status_driver;
  const riderStatus = (typeof riderStatusValue === 'string' && riderStatuses.has(riderStatusValue))
    ? riderStatusValue as RiderRideStatus
    : (
        normalizedStatus === 'ended' || normalizedStatus === 'completed'
          ? 'ended'
          : normalizedStatus === 'ongoing'
            ? 'ongoing'
            : derivedStatuses.rider
      );
  const driverStatus = (typeof driverStatusValue === 'string' && driverStatuses.has(driverStatusValue))
    ? driverStatusValue as DriverRideStatus
    : (
        normalizedStatus === 'completed' || normalizedStatus === 'ended'
          ? 'completed'
          : normalizedStatus === 'ongoing'
            ? 'ongoing'
            : derivedStatuses.driver
      );

  return {
    id: ride?.id ?? '',
    driver: normalizeUser(driverRecord),
    destination: ride?.destination ?? 'other',
    destinationName: ride?.destinationName ?? ride?.destination_name ?? '',
    pickupLocation: ride?.pickupLocation ?? ride?.pickup_location ?? '',
    date,
    startTime,
    endTime,
    seatsAvailable,
    seatsTotal,
    gasSplitCost: Number(ride?.gasSplitCost ?? ride?.gas_split_cost ?? 0),
    riders,
    status: normalizedStatus,
    tripStatus: normalizedStatus,
    tripStatusRider: riderStatus,
    tripStatusDriver: driverStatus,
    requests,
    totalRequests: Number(ride?.totalRequests ?? ride?.total_requests ?? requests.length),
  };
};

const normalizeConversation = (conversation?: ApiRecord): Conversation => ({
  id: conversation?.id ?? '',
  ride: conversation?.ride ? normalizeRide(conversation.ride) : undefined,
  participants: Array.isArray(conversation?.participants)
    ? conversation.participants.map(normalizeUser)
    : [],
  messages: Array.isArray(conversation?.messages)
    ? conversation.messages.map(normalizeMessage)
    : [],
  lastMessage: conversation?.lastMessage ? normalizeMessage(conversation.lastMessage) : undefined,
  unreadCount: Number(conversation?.unreadCount ?? conversation?.unread_count ?? 0),
  updatedAt: conversation?.updatedAt ?? conversation?.updated_at,
  currentUserId: conversation?.currentUserId ?? conversation?.current_user_id,
});

const normalizeFeedPost = (post?: ApiRecord): FeedPost => ({
  id: post?.id ?? '',
  authorId: post?.authorId ?? post?.author_id ?? post?.user_id ?? '',
  type: post?.type === 'trade' ? 'trade' : 'party',
  author: normalizeUser(post?.author ?? post?.user),
  title: post?.title ?? '',
  description: post?.description ?? '',
  location: post?.location,
  date: post?.date,
  time: post?.time,
  price: post?.price !== undefined ? Number(post.price) : undefined,
  images: Array.isArray(post?.images) ? post.images : [],
  createdAt: post?.createdAt ?? post?.created_at ?? new Date().toISOString(),
});

const normalizeDirectoryUser = (user?: ApiRecord): SuperAdminDirectoryUser => ({
  ...normalizeUser(user),
  createdAt: user?.createdAt ?? user?.created_at,
});

const extractRides = <T extends ApiRecord>(response: MaybeWrappedList<T>): T[] => {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.rides) ? response.rides : [];
};

const extractRide = <T extends ApiRecord>(response: MaybeWrappedRide<T>): T | undefined => {
  if (response && typeof response === 'object' && 'ride' in response) {
    return response.ride;
  }
  return response as T;
};

// ============ Auth API ============

export const authApi = {
  register: (payload: RegisterPayload): Promise<MagicLinkResponse> =>
    apiClient.post('/auth/register', payload, { skipAuth: true }),

  login: (payload: LoginPayload): Promise<MagicLinkResponse> =>
    apiClient.post('/auth/login', payload, { skipAuth: true }),

  verifyMagicLink: async (token: string): Promise<AuthResponse> => {
    try {
      return await apiClient.get('/auth/verify', {
        params: { token },
        skipAuth: true,
        timeout: MAGIC_LINK_VERIFY_TIMEOUT_MS,
      });
    } catch (error) {
      if (error instanceof ApiClientError && error.message === 'Request timed out') {
        console.warn('[Magic Link] Verify request timed out, retrying once...');
        return apiClient.get('/auth/verify', {
          params: { token },
          skipAuth: true,
          timeout: MAGIC_LINK_VERIFY_TIMEOUT_MS,
        });
      }

      throw error;
    }
  },

  resendMagicLink: (email: string): Promise<MagicLinkResponse> =>
    apiClient.post('/auth/resend', { email }, { skipAuth: true }),

  getMe: (): Promise<ProfileResponse> =>
    apiClient.get('/auth/me', { suppressUnauthorizedRedirect: true }),
};

export const adminApi = {
  login: (payload: AdminLoginPayload): Promise<AdminLoginResponse> =>
    apiClient.post('/auth/admin/login', payload, { skipAuth: true }),

  getSchoolDashboard: (): Promise<SchoolAdminDashboardData> =>
    apiClient
      .get<SchoolAdminDashboardResponse>('/admin/dashboard')
      .then((res) => ({
        role: res.role,
        school: {
          schoolDomain: res.school?.schoolDomain ?? (res.school as ApiRecord | undefined)?.school_domain ?? '',
          schoolName: res.school?.schoolName ?? (res.school as ApiRecord | undefined)?.school_name ?? '',
        },
        stats: {
          students: Number(res.stats?.students ?? 0),
          drivers: Number(res.stats?.drivers ?? 0),
          riders: Number(res.stats?.riders ?? 0),
          rides: Number(res.stats?.rides ?? 0),
          activeRides: Number(res.stats?.activeRides ?? 0),
          posts: Number(res.stats?.posts ?? 0),
        },
        students: Array.isArray(res.students) ? res.students.map(normalizeUser) : [],
        rides: Array.isArray(res.rides) ? res.rides.map(normalizeRide) : [],
        posts: Array.isArray(res.posts) ? res.posts.map(normalizeFeedPost) : [],
      })),

  deleteStudent: (userId: string): Promise<void> =>
    apiClient.delete(`/admin/students/${userId}`),

  deleteRide: (rideId: string): Promise<void> =>
    apiClient.delete(`/admin/rides/${rideId}`),

  deletePost: (postId: string): Promise<void> =>
    apiClient.delete(`/admin/posts/${postId}`),

  getSuperStats: (): Promise<SuperAdminStatsData> =>
    apiClient
      .get<SuperAdminStatsResponse>('/admin/super/stats')
      .then((res) => ({
        stats: res.stats,
        users: {
          all: Array.isArray(res.users?.all) ? res.users.all.map(normalizeDirectoryUser) : [],
          drivers: Array.isArray(res.users?.drivers) ? res.users.drivers.map(normalizeDirectoryUser) : [],
          riders: Array.isArray(res.users?.riders) ? res.users.riders.map(normalizeDirectoryUser) : [],
          admins: Array.isArray(res.users?.admins) ? res.users.admins.map(normalizeDirectoryUser) : [],
          superAdmins: Array.isArray(res.users?.superAdmins) ? res.users.superAdmins.map(normalizeDirectoryUser) : [],
        },
        schools: Array.isArray(res.schools) ? res.schools.map((school) => ({
          schoolDomain: school.schoolDomain ?? (school as ApiRecord).school_domain ?? '',
          schoolName: school.schoolName ?? (school as ApiRecord).school_name ?? '',
          totalUsers: Number(school.totalUsers ?? 0),
          totalStudents: Number(school.totalStudents ?? 0),
          totalAdmins: Number(school.totalAdmins ?? 0),
          totalRides: Number(school.totalRides ?? 0),
          totalPosts: Number(school.totalPosts ?? 0),
        })) : [],
        reminders: {
          availableScopes: Array.isArray(res.reminders?.availableScopes) ? res.reminders.availableScopes : [],
          sendEndpoint: res.reminders?.sendEndpoint ?? '',
        },
      })),

  getAdminAccounts: (): Promise<AdminListItem[]> =>
    apiClient
      .get<{ success?: boolean; admins?: ApiRecord[] } | ApiRecord[]>('/admin/super/admins')
      .then((res) => Array.isArray(res) ? res : (res.admins || []))
      .then((admins) => admins.map(normalizeUser)),

  createAdminAccount: (payload: CreateAdminPayload): Promise<AdminListItem> =>
    apiClient
      .post<{ success?: boolean; message?: string; admin?: ApiRecord } | ApiRecord>('/admin/super/admins', payload)
      .then((res) => ('admin' in res ? res.admin : res))
      .then((admin) => normalizeUser(admin)),

  deleteUser: (userId: string): Promise<void> =>
    apiClient.delete(`/admin/super/users/${userId}`),

  sendReminder: (payload: SendSuperAdminReminderPayload): Promise<SendSuperAdminReminderResponse> =>
    apiClient.post('/admin/super/reminders/send', payload),
};

// ============ Rides API ============

export const ridesApi = {
  getAvailableRides: (): Promise<Ride[]> =>
    apiClient
      .get<MaybeWrappedList<ApiRecord>>('/rides/available')
      .then(res => extractRides(res).map(normalizeRide))
      .then(rides => rides.filter(ride => ride.tripStatusRider !== 'ended' && ride.status !== 'cancelled')),

  getMyRides: (): Promise<RideWithRequests[]> =>
    apiClient
      .get<MaybeWrappedList<ApiRecord>>('/rides/my-rides')
      .then(res => extractRides(res).map(normalizeRide)),

  createRide: (payload: CreateRidePayload): Promise<Ride> =>
    apiClient
      .post<MaybeWrappedRide<ApiRecord>>('/rides', payload)
      .then(res => {
        const ride = extractRide(res);
        if (!ride) {
          throw new Error('Ride was created but no ride payload was returned');
        }
        return normalizeRide(ride);
      }),

  deleteRide: (rideId: string): Promise<void> =>
    apiClient.delete(`/rides/${rideId}`),

  requestSeat: (rideId: string): Promise<RideRequest> =>
    apiClient.post<RideRequestResponse>(`/rides/${rideId}/request`).then(res => res.request),

  getMyRequests: (): Promise<RideRequest[]> =>
    apiClient
      .get<{ success?: boolean; requests?: ApiRecord[] } | ApiRecord[]>('/rides/my-requests')
      .then((res) => Array.isArray(res) ? res : (res.requests || []))
      .then((requests) => requests.map(normalizeRideRequest)),

  getRideRequests: (rideId: string): Promise<RideRequest[]> =>
    apiClient
      .get<RideRequestsListResponse | ApiRecord[]>(`/rides/${rideId}/requests`)
      .then((res) => Array.isArray(res) ? res : (res.requests || []))
      .then((requests) => requests.map(normalizeRideRequest)),

  respondToRequest: (
    rideId: string,
    requestId: string,
    action: RideRequestAction
  ): Promise<RideRequest> =>
    apiClient
      .patch<RideRequestResponse | { success?: boolean; request?: ApiRecord }>(
        `/rides/${rideId}/requests/${requestId}/respond`,
        { action }
      )
      .then((res) => ('request' in res ? res.request : undefined))
      .then((request) => {
        if (!request) {
          throw new Error('Updated request payload was not returned');
        }
        return normalizeRideRequest(request);
      }),

  updateRideStatus: (
    rideId: string,
    status: RideStatus
  ): Promise<Ride> =>
    apiClient.put<MaybeWrappedRide<ApiRecord>>(`/rides/${rideId}/status`, { status })
      .then(res => {
        const ride = extractRide(res);
        if (!ride) {
          throw new Error('Updated ride payload was not returned');
        }
        return normalizeRide(ride);
      }),

  updateRide: (rideId: string, payload: Partial<CreateRidePayload>): Promise<Ride> =>
    apiClient.patch<MaybeWrappedRide<ApiRecord>>(`/rides/${rideId}`, payload).then(res => {
      const ride = extractRide(res);
      if (!ride) {
        throw new Error('Updated ride payload was not returned');
      }
      return normalizeRide(ride);
    }),
};

export type RiderRideRequestAction = 'accept' | 'reject' | 'cancel';

export const riderRideRequestsApi = {
  create: (payload: CreateRiderRideRequestPayload): Promise<RiderRideRequest> =>
    apiClient
      .post<{ success?: boolean; request?: ApiRecord }>('/ride-requests', payload)
      .then((res) => {
        if (!res.request) throw new Error('Ride request payload was not returned');
        return normalizeRiderRideRequest(res.request);
      }),

  getMine: (): Promise<RiderRideRequest[]> =>
    apiClient
      .get<RiderRideRequestsResponse>('/ride-requests/mine')
      .then(extractRiderRideRequests)
      .then((requests) => requests.map(normalizeRiderRideRequest)),

  getAvailable: (): Promise<RiderRideRequest[]> =>
    apiClient
      .get<RiderRideRequestsResponse>('/ride-requests/available', {
        params: { _: Date.now() },
      })
      .then(extractRiderRideRequests)
      .then((requests) => requests.map(normalizeRiderRideRequest)),

  remove: (requestId: string): Promise<void> =>
    apiClient.delete(`/ride-requests/${requestId}`),

  respond: (requestId: string, action: RiderRideRequestAction): Promise<RiderRideRequest> =>
    apiClient
      .patch<{ success?: boolean; request?: ApiRecord }>(`/ride-requests/${requestId}/respond`, { action })
      .then((res) => {
        if (!res.request) throw new Error('Updated ride request payload was not returned');
        return normalizeRiderRideRequest(res.request);
      }),
};

// ============ Messages API ============

export const messagesApi = {
  getConversations: (): Promise<Conversation[]> =>
    apiClient
      .get<ConversationsResponse | { success?: boolean; conversations?: ApiRecord[] } | ApiRecord[]>('/messages/conversations')
      .then((res) => Array.isArray(res) ? res : (res.conversations || []))
      .then((conversations) => conversations.map(normalizeConversation)),

  startConversation: (payload: StartConversationPayload): Promise<Conversation> =>
    apiClient
      .post<ConversationResponse | { success?: boolean; conversation?: ApiRecord }>('/messages/conversations', payload)
      .then((res) => {
        const conversation = 'conversation' in res ? res.conversation : undefined;
        if (!conversation) {
          throw new Error('Conversation payload was not returned');
        }
        return normalizeConversation(conversation);
      }),

  getMessages: (conversationId: string): Promise<Message[]> =>
    apiClient.get<MessagesResponse>(`/messages/conversations/${conversationId}/messages`)
      .then(res => (res.messages || []).map(normalizeMessage)),

  sendMessage: (conversationId: string, payload: SendMessagePayload): Promise<Message> =>
    apiClient.post<MessageResponse>(`/messages/conversations/${conversationId}/messages`, payload)
      .then(res => normalizeMessage(res.message)),

  markAsRead: (conversationId: string): Promise<void> =>
    apiClient.patch(`/messages/conversations/${conversationId}/read`),
};

// ============ Feed API ============

export const feedApi = {
  getPosts: (): Promise<FeedPost[]> =>
    apiClient.get<PostsResponse>('/feed').then(res => res.posts || []),

  createPost: (payload: CreateFeedPostPayload): Promise<FeedPost> =>
    apiClient.post<PostResponse>('/feed', payload).then(res => res.post),

  uploadImage: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('collegeryde_token');
    const requestUrl = `${normalizedBaseUrl}/feed/upload`;
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Upload failed');
    return data.url;
  },

  deletePost: (postId: string): Promise<void> =>
    apiClient.delete(`/feed/${postId}`),
};

// ============ Profile API ============

export const profileApi = {
  getProfile: (userId: string): Promise<UserProfile> =>
    apiClient.get<ProfileResponse>(`/users/${userId}/profile`).then(res => res.user),

  updateProfile: (payload: UpdateProfilePayload): Promise<UserProfile> =>
    apiClient.put<ProfileResponse>('/users/profile', payload).then(res => res.user),

  uploadAvatar: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('avatar', file);

    const token = localStorage.getItem('collegeryde_token');
    const requestUrl = `${normalizedBaseUrl}/users/avatar`;
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Upload failed');
    return data.url;
  },
};

// ============ Notifications API ============

export const notificationsApi = {
  getNotifications: (): Promise<Notification[]> =>
    apiClient.get<NotificationsResponse>('/notifications').then(res => res.notifications || []),

  markAsRead: (notificationId: string): Promise<void> =>
    apiClient.put(`/notifications/${notificationId}/read`),

  markAllAsRead: (): Promise<void> =>
    apiClient.put('/notifications/read-all'),
};

export default apiClient;
